import { useParams } from 'react-router-dom';

import { EmptyState } from '@components/empty-state';
import { IconJourneyItem } from '@components/icons/journey-icon';
import {
  IntegrationDetail as CoreIntegrationDetail,
  initializeConditionHelpers,
  useIntegration,
} from '@components/integrations';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useUserOption } from 'api/authentication/hooks';
import { useIntegrationJourneys } from 'api/integrations/hooks';
import { JourneyListItem } from 'ui/authenticated/main/pages/journeys/journey-item';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

initializeConditionHelpers();

const JourneysTabContent = () => {
  const { t } = useScopedTranslate(locale);
  const { integrationId } = useParams<{ integrationId: string }>();
  const journeysQuery = useIntegrationJourneys(integrationId!);

  if (!journeysQuery.data) {
    return <Spinner />;
  }

  return (
    <PageContent>
      <Stack space={2}>
        {journeysQuery.data.length === 0 ? (
          <EmptyState
            skipMargin
            icon={<IconJourneyItem size={80} />}
            header={t('noJourneys')}
            content={t('noJourneysDescription')}
          />
        ) : (
          <Stack space={2}>
            {journeysQuery.data.map((journey) => (
              <div key={journey.shortId}>
                <JourneyListItem journey={journey} hideMenu />
              </div>
            ))}
          </Stack>
        )}
      </Stack>
    </PageContent>
  );
};

export const IntegrationDetail = () => {
  const { t } = useScopedTranslate({
    en: {
      journeys: 'Journeys',
    },
  });
  const { integrationId } = useParams<{ integrationId: string }>();
  const { isEnabled: hasWebhooks } = useFeature('FEATURE_INTEGRATION_WEBHOOKS');
  const { value: hasHidden, update } = useUserOption('hasHiddenIntegrationWizard', false);
  const integrationQuery = useIntegration(integrationId!);

  if (!integrationQuery.data) {
    return <Spinner />;
  }

  const journeysTab = {
    content: <JourneysTabContent />,
    counter: integrationQuery.data.numJourneys,
    label: t('journeys'),
    section: 'journeys',
  };

  return (
    <CoreIntegrationDetail
      integrationId={integrationId}
      backUrl={routePaths.integrations}
      extraTabs={[journeysTab]}
      hasWebhooks={hasWebhooks}
      onDismissSetupHelper={() => update.mutate(true)}
      hasHiddenSetupHelper={hasHidden}
    />
  );
};

const locale = {
  en: {
    noJourneys: 'No journeys',
    noJourneysDescription: 'This integration is currently not used in any journeys',
  },
};
